/**
 * Hero section component for Stack Forward website
 */

import React from 'react';

/**
 * Badge interface
 */
interface Badge {
  text: string;
  icon?: string;
}

/**
 * Hero section component
 */
export const Hero: React.FC = () => {
  // Badge data
  const badges: Badge[] = [
    { text: 'On-Prem & Self-Hosted', icon: '🏢' },
    { text: 'Middle East & India', icon: '🌍' },
    { text: 'Security-First', icon: '🔒' },
  ];

  // Handle CTA clicks
  const handlePrimaryCTA = () => {
    const contactSection = document.querySelector('#contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSecondaryCTA = () => {
    const solutionsSection = document.querySelector('#solutions');
    if (solutionsSection) {
      solutionsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-50 to-white overflow-hidden pt-16">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary-100 rounded-full -translate-y-48 translate-x-48 opacity-20"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-primary-200 rounded-full translate-y-40 -translate-x-40 opacity-20"></div>

      <div className="container-custom relative z-10 py-8 md:py-12 lg:py-12 xl:py-8">
        <div className="max-w-4xl mx-auto text-center">
          {/* Main headline */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-neutral-900 mb-4 sm:mb-5 md:mb-6 animate-fade-in px-4">
            Open-Source Systems for{' '}
            <span className="text-gradient">Businesses of All Sizes</span>
          </h1>

          {/* Subheadline */}
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-neutral-600 mb-6 sm:mb-7 md:mb-8 max-w-3xl mx-auto leading-relaxed animate-slide-up px-4">
            We implement, host, and support{' '}
            <span className="font-semibold text-neutral-800">ERPNext</span>,{' '}
            <span className="font-semibold text-neutral-800">Odoo</span>,{' '}
            <span className="font-semibold text-neutral-800">Mattermost</span>,{' '}
            <span className="font-semibold text-neutral-800">Nextcloud</span>{' '}
            and more —{' '}
            <span className="text-primary-600 font-semibold">
              on-prem or your cloud
            </span>
            .
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-8 sm:mb-10 md:mb-12 animate-slide-up px-4">
            <button
              onClick={handlePrimaryCTA}
              className="btn-primary text-sm sm:text-base md:text-lg px-6 py-3 sm:px-8 sm:py-4 w-full sm:w-auto"
              aria-label="Get a technical assessment"
            >
              Get a Technical Assessment
            </button>
            <button
              onClick={handleSecondaryCTA}
              className="btn-secondary text-sm sm:text-base md:text-lg px-6 py-3 sm:px-8 sm:py-4 w-full sm:w-auto"
              aria-label="See our solutions"
            >
              See Solutions
            </button>
          </div>

          {/* Badge row */}
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 animate-slide-up px-4">
            {badges.map((badge, index) => (
              <div
                key={index}
                className="flex items-center space-x-2 bg-white/80 backdrop-blur-sm border border-neutral-200 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 shadow-sm"
              >
                {badge.icon && (
                  <span className="text-base sm:text-lg" aria-hidden="true">
                    {badge.icon}
                  </span>
                )}
                <span className="text-xs sm:text-sm font-medium text-neutral-700 whitespace-nowrap">
                  {badge.text}
                </span>
              </div>
            ))}
          </div>

          {/* Trust indicators */}
          <div className="mt-6 sm:mt-8 md:mt-12 lg:mt-12 xl:mt-8 pt-3 sm:pt-4 md:pt-6 lg:pt-6 xl:pt-4 border-t border-neutral-200">
            <p className="text-xs sm:text-sm text-neutral-500 mb-3 sm:mb-4">
              Trusted by teams across industries
            </p>
            <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6 md:gap-8 opacity-60 px-4">
              <div className="text-lg sm:text-xl md:text-2xl font-bold text-neutral-400">
                Manufacturing
              </div>
              <div className="text-lg sm:text-xl md:text-2xl font-bold text-neutral-400">
                Healthcare
              </div>
              <div className="text-lg sm:text-xl md:text-2xl font-bold text-neutral-400">
                Finance
              </div>
              <div className="text-lg sm:text-xl md:text-2xl font-bold text-neutral-400">
                Government
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-2 sm:bottom-4 md:bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce-subtle">
        <div className="w-5 h-8 sm:w-6 sm:h-10 border-2 border-neutral-400 rounded-full flex justify-center">
          <div className="w-1 h-2 sm:h-3 bg-neutral-400 rounded-full mt-1.5 sm:mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};
